import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, MapPin, ShieldCheck, Sparkles, Compass, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PublicNav } from '@/components/public/PublicNav';
import { Footer } from '@/components/public/Footer';
import api from '@/lib/api';
import { formatDate, formatBRL } from '@/lib/format';

const HERO = 'https://images.unsplash.com/photo-1470214203634-e436a8848e23?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1400';
const COLLAGE = [
  'https://images.unsplash.com/photo-1540541338287-41700207dee6?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=800',
  'https://images.pexels.com/photos/33404422/pexels-photo-33404422.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  'https://images.unsplash.com/photo-1629979445570-38457bf05e62?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=800',
];

const fade = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const UPCOMING = ['planejada', 'confirmada'];

export default function Home() {
  const [stats, setStats] = useState({ viagens_realizadas: 0, destinos: 0, clientes_atendidos: 0 });
  const [trips, setTrips] = useState([]);

  useEffect(() => {
    api.get('/public/stats').then((r) => setStats(r.data)).catch(() => {});
    api.get('/public/trips').then((r) => setTrips(r.data || [])).catch(() => {});
  }, []);

  // Prioriza viagens que ainda vão acontecer (mais próximas primeiro); senão, mostra as já realizadas
  const upcoming = useMemo(
    () => trips.filter((t) => UPCOMING.includes(t.status))
      .sort((a, b) => (a.data_inicio || '').localeCompare(b.data_inicio || '')),
    [trips]);
  const executed = useMemo(() => trips.filter((t) => t.status === 'executada'), [trips]);
  const showUpcoming = upcoming.length > 0;
  const featured = (showUpcoming ? upcoming : executed).slice(0, 3);
  const featuredTitle = showUpcoming ? 'Próximas viagens' : 'Viagens que já realizamos';
  const galleryLink = showUpcoming ? '/galeria?tab=proximas' : '/galeria';

  return (
    <div className="min-h-screen flex flex-col">
      <PublicNav />

      {/* HERO */}
      <section className="relative hero-wash">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16 lg:pt-20 lg:pb-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <motion.div initial="hidden" animate="show" variants={fade} className="lg:col-span-6">
            <span className="inline-flex items-center gap-2 text-xs font-medium px-3 py-1 rounded-full bg-accent text-accent-foreground">
              <Sparkles className="h-3.5 w-3.5" /> Agência de viagens e turismo
            </span>
            <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]">
              Sua próxima viagem começa com uma boa história
            </h1>
            <p className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-xl">
              Roteiros sob medida, fornecedores parceiros e atendimento de concierge. Veja as viagens que
              já realizamos e planeje a sua com a gente.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg" data-testid="public-hero-primary-cta">
                <Link to="/contato">Planejar nova viagem <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
              <Button asChild variant="secondary" size="lg" data-testid="public-hero-secondary-cta">
                <Link to={galleryLink}>Ver portfólio de viagens</Link>
              </Button>
            </div>
          </motion.div>

          <motion.div initial="hidden" animate="show" variants={fade} className="lg:col-span-6">
            <div className="grid grid-cols-2 gap-4">
              <img src={HERO} alt="Destino" className="col-span-2 h-56 w-full object-cover rounded-2xl card-shadow" />
              <img src={COLLAGE[1]} alt="Praia" className="h-40 w-full object-cover rounded-2xl card-shadow" />
              <img src={COLLAGE[2]} alt="Paisagem" className="h-40 w-full object-cover rounded-2xl card-shadow" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* STATS */}
      <section className="border-y border-border/60 bg-card">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-3 gap-4 text-center">
          {[
            { v: stats.viagens_realizadas, l: 'Viagens realizadas' },
            { v: stats.destinos, l: 'Destinos' },
            { v: stats.clientes_atendidos, l: 'Clientes atendidos' },
          ].map((s, i) => (
            <div key={i}>
              <div className="font-display text-3xl sm:text-4xl font-semibold text-primary tabular-nums">{s.v}+</div>
              <div className="text-xs sm:text-sm text-muted-foreground mt-1">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED TRIPS */}
      <section className="py-16 lg:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between gap-4 mb-8">
            <div>
              <h2 className="text-base md:text-lg font-medium text-muted-foreground">Portfólio</h2>
              <h3 className="font-display text-2xl sm:text-3xl font-semibold">{featuredTitle}</h3>
            </div>
            <Button asChild variant="ghost" className="hidden sm:inline-flex">
              <Link to={galleryLink}>Ver tudo <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
          </div>

          {featured.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border p-12 text-center">
              <Compass className="h-8 w-8 mx-auto text-muted-foreground" />
              <p className="mt-3 text-muted-foreground">Em breve novas viagens publicadas aqui.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {featured.map((t) => (
                <Link to={galleryLink} key={t.trip_id} className="group rounded-2xl overflow-hidden card-shadow card-shadow-hover bg-card" data-testid="home-trip-card">
                  <div className="h-52 w-full overflow-hidden bg-muted">
                    {t.fotos && t.fotos[0] ? (
                      <img src={t.fotos[0]} alt={t.nome} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.04]" />
                    ) : (
                      <div className="h-full w-full section-wash flex items-center justify-center"><Compass className="h-8 w-8 text-primary/40" /></div>
                    )}
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><MapPin className="h-3.5 w-3.5" /> {t.destino}</div>
                    <h4 className="mt-1.5 font-semibold text-lg">{t.nome}</h4>
                    {t.data_inicio && <p className="mt-1 text-sm text-muted-foreground">{showUpcoming ? 'Prevista para ' : ''}{formatDate(t.data_inicio)}</p>}
                    {t.preco_venda != null && t.preco_venda > 0 && (
                      <div className="mt-2 text-primary font-semibold">A partir de {formatBRL(t.preco_venda)}</div>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* PILLARS */}
      <section className="py-12 section-wash">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { icon: Compass, t: 'Curadoria', d: 'Roteiros desenhados sob medida para o seu perfil e orçamento.' },
            { icon: ShieldCheck, t: 'Segurança', d: 'Fornecedores parceiros confiáveis e suporte em toda a viagem.' },
            { icon: Star, t: 'Experiência', d: 'Atendimento próximo, do primeiro contato ao retorno para casa.' },
          ].map((p, i) => (
            <div key={i} className="rounded-2xl bg-card p-6 card-shadow">
              <span className="h-11 w-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center"><p.icon className="h-5 w-5" /></span>
              <h4 className="mt-4 font-semibold text-lg">{p.t}</h4>
              <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{p.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">Pronto para a próxima aventura?</h3>
          <p className="mt-4 text-muted-foreground">Conte para a gente o destino dos seus sonhos. Cuidamos de todo o resto.</p>
          <Button asChild size="lg" className="mt-8" data-testid="public-cta-button">
            <Link to="/contato">Falar com um especialista <ArrowRight className="ml-2 h-4 w-4" /></Link>
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
